import { useAuth0 } from "@auth0/auth0-react";
import { Box, SxProps, Typography } from "@mui/material";
import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AdminDeleteCandidates from "../Admin/Candidates/Candidates";
import ViewCandidate from "../Admin/Candidates/ViewCandidate/ViewCandidate";
import JobsRedirect from "../Admin/Jobs/Jobs";
import AdminMessaging from "../Admin/Messaging/Messaging";
import { FormProfile } from "../CampaignProfile/FormProfile";
import { CandidateGroups } from "../CandidateGroups/CandidateGroups";
import CandidateProfile from "../CandidateProfile/CandidateProfile";
import CreateCampaigns from "../CreateCampaign/CreateCampaigns";
import Dashboard from "../Dashboard/Dashboard";
import { DynamicGroup } from "../DynamicGroup/DynamicGroup";
import { GroupProfile } from "../GroupProfile/GroupProfile";
import Imports from "../Imports/Imports";
import Campaigns from "../ManageCampaigns/ManageCampaigns";
import Candidates from "../ManageCandidates/ManageCandidates";
import CommunicationTemplates from "../MessagingCenter/CommunicationTemplates/CommunicationTemplates";
import EmailMarketing from "../MessagingCenter/EmailMarketing/EmailMarketing";
import TextMessages from "../MessagingCenter/TextMessages/TextMessages";
import UserProfile from "../UserProfile/UserProfile";
import NavBar from "./Components/Navigation/NavBar";
import { RenderSnackbar } from "./Components/Snackbar/Snackbar";
import { CampaignPageActions } from "./Enums/CampaignTypes";
import { useClients } from "./Queries/ClientQueries";
import { useGetUserProfileQuery } from "./Queries/UserProfileQueries";
import { useCurrentUser } from "./Queries/UserQueries";
import { useFeatureToggles } from "./State/Hooks/useFeatureToggles";
import { useImportStatusNotifications } from "./State/Hooks/useImportStatusNotifications";
import { useUserProfileStore } from "./State/Stores/UserProfileStore";
import "./Styles/App.css";
import { useNotification } from "./State/Hooks/notificationHooks";
import { useNotificationsHub } from "./State/Hooks/notificationsHubHook";
import { NoAccessPage } from "./Components/NoAccess/NoAccessPage";
import { useCrmAccess } from "./State/Hooks/useCrmAccess";

export default function App() {
  const { isLoading: isLoadingAuth0Data, loginWithRedirect, isAuthenticated, error } = useAuth0();
  const user = useCurrentUser();
  useClients();
  const access = useCrmAccess();
  const { setProfile, profile } = useUserProfileStore();
  const { data: requestedProfile, isFetched: isProfileFetched } = useGetUserProfileQuery(user.id);
  const { displaySuccess, displayError } = useNotification();
  const notificationsHub = useNotificationsHub();

  useEffect(() => {
    if (requestedProfile && isProfileFetched) {
      requestedProfile.firstName = user.firstName;
      requestedProfile.lastName = user.lastName;
      requestedProfile.email = user.email;
      setProfile(requestedProfile);
    }
  }, [isProfileFetched, profile, requestedProfile, setProfile]);

  useEffect(() => {
    if (notificationsHub) {
      notificationsHub.on("ReceiveNotification", (success: boolean, message: any) => {
        if (success) {
          displaySuccess(message);
        } else {
          displayError(message);
        }
      });

      return () => {
        notificationsHub.off("ReceiveNotification");
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsHub, notificationsHub?.state]);

  useFeatureToggles();

  if (isLoadingAuth0Data) {
    return <TransitionPage message="Loading..." />;
  }

  if (error) {
    return <TransitionPage message="Oops..." messageSx={{ color: "error.main" }} caption={error.message} />;
  }

  if (isAuthenticated) {
    if (access === "loading") return <TransitionPage message="Loading..." />;
    if (access === "error") {
      return (
        <TransitionPage
          message="Oops..."
          messageSx={{ color: "error.main" }}
          caption="We couldn't load your account. Please refresh to try again."
        />
      );
    }
    if (access === "noAccess") return <NoAccessPage />;
    return <AppRoutes userHasAccess />;
  } else {
    loginWithRedirect({
      appState: { returnTo: `${window.location.pathname}${window.location.search}` },
    });
    return <TransitionPage message="Redirecting..." />;
  }
}

function AppRoutes({ userHasAccess }: { userHasAccess: boolean }) {
  useImportStatusNotifications(); // This is a custom hook that sets up notification logic for ongoing imports based on the current route
  return (
    <>
      <NavBar userHasAccess={userHasAccess} />
      <Routes>
        <Route index element={<Dashboard userHasAccess={userHasAccess} />} />
        <Route path="*" element={<Navigate to="/" />} />
        {userHasAccess ? (
          <>
            <Route path="candidates" element={<Candidates />} />
            <Route path="candidates/:id" element={<CandidateProfile />} />
            <Route path="candidates/imports" element={<Imports />} />
            <Route path="candidategroups/:id/imports" element={<Imports />} />
            <Route path="candidategroups" element={<CandidateGroups />} />
            <Route path="candidategroups/:id" element={<GroupProfile />} />
            <Route path="candidategroups/edit/:id" element={<DynamicGroup />} />
            <Route path="leadcapture/forms" element={<Campaigns />} />
            <Route
              path="leadcapture/forms/create"
              element={<CreateCampaigns action={CampaignPageActions.Create} />}
            />
            <Route path="leadcapture/forms/:id" element={<FormProfile />} />
            <Route
              path="leadcapture/forms/edit/:campaignId/:formId"
              element={<CreateCampaigns action={CampaignPageActions.Edit} />}
            />
            <Route
              path="leadcapture/forms/duplicate/:campaignId/:formId"
              element={<CreateCampaigns action={CampaignPageActions.Duplicate} />}
            />
            <Route path="profile" element={<UserProfile />} />
            <Route path="messaging/email" element={<EmailMarketing />} />
            <Route path="messaging/text" element={<TextMessages />} />
            <Route path="messaging/templates" element={<CommunicationTemplates />} />
            <Route path="admin/jobs" element={<JobsRedirect />} />
            <Route path="admin/messaging" element={<AdminMessaging />} />
            <Route path="admin/candidates" element={<AdminDeleteCandidates />} />
            <Route path="admin/candidates/:id" element={<ViewCandidate />} />
          </>
        ) : null}
      </Routes>
      <RenderSnackbar />
    </>
  );
}

export function TransitionPage({
  message,
  messageSx,
  caption,
  boxSx,
}: {
  message: string;
  messageSx?: SxProps;
  caption?: string;
  boxSx?: SxProps;
}) {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...boxSx,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
        <Typography
          sx={{
            fontFamily: "h1.fontFamily",
            color: "secondary.main",
            fontWeight: 700,
            fontSize: "3rem",
            ...messageSx,
          }}
        >
          {message}
        </Typography>
        {caption && <Typography>{caption}</Typography>}
      </Box>
    </Box>
  );
}
